
import { Injectable, inject } from '@angular/core';
import { Plan, Subscription, UsageStats, StoreWallet, WalletTransaction } from '../models/index';
import { getSupabase, isSupabaseConfigured } from '../firebase';
import { SupabaseClient } from '@supabase/supabase-js';
import { AuthService } from './auth.service';

@Injectable({ providedIn: 'root' })
export class BillingService {
  private supabase: SupabaseClient | null = getSupabase();
  private authService = inject(AuthService);

  async getPlans(): Promise<Plan[]> {
    if (!isSupabaseConfigured() || !this.supabase) { 
        return [
            { id: 'tier_s', name: 'Tier S (Starter)', price_monthly: 1500, interval: 'month', features: { telemedicine: true }, limits: { users: 2, medical_cases: 50, overage_cost: 50 } },
            { id: 'tier_m', name: 'Tier M (Growth)', price_monthly: 3500, interval: 'month', features: { telemedicine: true }, limits: { users: 5, medical_cases: 150, overage_cost: 25 } },
            { id: 'tier_d', name: 'Tier D (Dispensary)', price_monthly: 990, interval: 'month', features: { core_pos: true }, limits: { users: 2, medical_cases: 20, overage_cost: 60 } }
        ]; 
    }
    const { data, error } = await this.supabase.from('plans').select('*');
    if (error) throw error;
    return data as Plan[];
  }

  async getSubscription(): Promise<Subscription | null> {
    if (!isSupabaseConfigured() || !this.supabase) { return null; }
    
    const storeId = this.authService.currentStore()?.id;
    if (!storeId) return null;

    const { data, error } = await this.supabase
      .from('subscriptions')
      .select('*, plan:plans(*)')
      .eq('store_id', storeId)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
      
    if (error) throw error;
    return data as Subscription;
  }

  async getUsageStats(): Promise<UsageStats> {
    const store = this.authService.currentStore();
    const now = new Date();
    const nextReset = new Date(now.getFullYear(), now.getMonth() + 1, 1);

    if (!isSupabaseConfigured() || !this.supabase) { 
        return { 
          medical_cases_used: 0, 
          users_active: 0, 
          period_start: now.toISOString(),
          next_reset_date: nextReset.toISOString()
        }; 
    }
    
    const { data, error } = await this.supabase.rpc('get_current_plan_usage');
    if (error) return { medical_cases_used: 0, users_active: 0, period_start: now.toISOString(), next_reset_date: nextReset.toISOString() };
    
    return data as UsageStats;
  }

  async getWalletBalance(): Promise<StoreWallet> {
      if (!isSupabaseConfigured() || !this.supabase) return { balance: 0, updated_at: new Date().toISOString() };
      const { data } = await this.supabase.rpc('get_store_wallet_balance');
      return { balance: Number(data || 0), updated_at: new Date().toISOString() };
  }

  async getWalletTransactions(): Promise<WalletTransaction[]> {
      if (!isSupabaseConfigured() || !this.supabase) return [];
      const { data } = await this.supabase.rpc('get_wallet_transactions');
      return (data || []) as WalletTransaction[];
  }

  async verifySlipWithApi(imageBase64: string, expectedAmount: number, planId?: string): Promise<{ success: boolean; message?: string }> {
    if (!this.supabase) throw new Error("Database not connected.");
    
    const { data: { session } } = await (this.supabase.auth as any).getSession();
    const token = session?.access_token || localStorage.getItem('custom_pin_token');

    try {
      const { data, error } = await this.supabase.functions.invoke('verify_slip', {
        body: { imageBase64, expectedAmount, planId },
        headers: token ? { Authorization: `Bearer ${token}` } : undefined
      });
      if (error) throw error;
      return { success: data.success, message: data.message };
    } catch (e: any) {
      return { success: false, message: "การเชื่อมต่อล้มเหลว หรือสลิปไม่ถูกต้อง" };
    }
  }

  async processOmiseCharge(token: string, amount: number, metadata: any = {}): Promise<any> {
    if (!this.supabase) throw new Error("Database not connected.");
    
    const { data: { session } } = await (this.supabase.auth as any).getSession();
    const activeToken = session?.access_token || localStorage.getItem('custom_pin_token');

    if (!activeToken) throw new Error("No active session found.");

    try {
        const { data, error } = await this.supabase.functions.invoke('omise_payment', {
            body: { 
                token: token,
                amount: amount, 
                currency: 'thb',
                metadata: { ...metadata, storeId: this.authService.currentStore()?.id }
            },
            headers: { Authorization: `Bearer ${activeToken}` }
        });

        if (error) throw new Error(`System Error: ${error.message}`);
        if (data && !data.success) throw new Error(data.error || 'Payment failed');
        return data.charge;
    } catch (e: any) {
        throw e;
    }
  }

  async manualCheckPayment(chargeId: string): Promise<{ success: boolean; message: string }> {
      if (!this.supabase) throw new Error("Database not connected.");
      const { data: { session } } = await (this.supabase.auth as any).getSession();
      const token = session?.access_token || localStorage.getItem('custom_pin_token');

      const { data, error } = await this.supabase.functions.invoke('admin_check_payment', {
          body: { chargeId },
          headers: token ? { Authorization: `Bearer ${token}` } : undefined
      });

      if (error) throw new Error(error.message);
      return data;
  }
}
